// Discount code service for APN
import { MEMBERSHIP_TIERS } from '../constants/membershipTiers';

// Available discount codes for early adopters and events
export const DISCOUNT_CODES = {
  // Membership discount codes
  "EARLYBIRD2025": {
    type: "percentage",
    value: 100, // 100% off = free
    description: "Early Adopter Free Membership",
    validUntil: "2025-12-31",
    maxUses: 100,
    membershipOnly: true,
    badge: "founding-member" // Badge to assign to users with this code
  },
  "FOUNDING2025": {
    type: "percentage",
    value: 100,
    description: "Founding Member Free Access",
    validUntil: "2025-12-31",
    maxUses: 50,
    membershipOnly: true,
    badge: "founding-member"
  },
  "TESTER2025": {
    type: "percentage",
    value: 100,
    description: "Beta Tester Free Membership",
    validUntil: "2025-12-31",
    maxUses: 25,
    membershipOnly: true,
    badge: "founding-member"
  },
  "FREEEVENT": {
    type: "percentage",
    value: 100,
    description: "Free Event Access",
    validUntil: "2025-12-31",
    maxUses: 1000,
    eventOnly: true
  },
  "ADMIN100": {
    type: "percentage",
    value: 100,
    description: "Admin Free Access",
    validUntil: "2025-12-31",
    maxUses: 999,
    // Works for both events and memberships
  },

  // Event discount codes (from WordPress site)
  "ADMIN10": {
    type: "percentage",
    value: 10,
    description: "10% admin discount",
    validUntil: "2026-12-31",
    maxUses: null, // Unlimited uses
    membershipOnly: false,
    eventOnly: true
  },
  "SPONSORS25": {
    type: "percentage",
    value: 25,
    description: "25% sponsor discount",
    validUntil: "2026-12-31",
    maxUses: null,
    membershipOnly: false,
    eventOnly: true
  },
  "RELAUNCH20": {
    type: "percentage",
    value: 20,
    description: "20% event special discount",
    validUntil: "2026-12-31",
    maxUses: null,
    membershipOnly: false,
    eventOnly: true
  }
};

// Validate discount code
export const validateDiscountCode = async (code, type = "membership") => {
  try {
    // First try client-side validation as fallback
    const clientSideValidation = validateDiscountCodeClientSide(code, type);
    if (clientSideValidation.valid) {
      return clientSideValidation;
    }

    // If client-side validation fails, try the Cloud Function
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/validateDiscountCodeEndpoint';

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: code.toUpperCase(),
        type
      }),
    });

    const result = await response.json();

    if (response.ok) {
      return {
        valid: true,
        discount: result.discount,
        discountAmount: result.discountAmount,
        discountType: result.discountType
      };
    } else {
      return {
        valid: false,
        error: result.error || 'Invalid discount code'
      };
    }
  } catch (error) {
    console.error('Error validating discount code, falling back to client-side validation:', error);

    // Fallback to client-side validation if Cloud Function fails
    return validateDiscountCodeClientSide(code, type);
  }
};

// Client-side validation fallback
const validateDiscountCodeClientSide = (code, type = "membership") => {
  const discountCode = DISCOUNT_CODES[code.toUpperCase()];

  if (!discountCode) {
    return { valid: false, error: "Invalid discount code" };
  }

  if (discountCode.membershipOnly && type !== "membership") {
    return { valid: false, error: "This discount code is only valid for memberships" };
  }

  if (discountCode.eventOnly && type !== "event") {
    return { valid: false, error: "This discount code is only valid for events" };
  }

  const now = new Date();
  const validUntil = new Date(discountCode.validUntil);

  if (now > validUntil) {
    return { valid: false, error: "Discount code has expired" };
  }

  return {
    valid: true,
    discount: discountCode,
    discountAmount: discountCode.value,
    discountType: discountCode.type
  };
};

// Calculate discounted price
export const calculateDiscountedPrice = (originalPrice, discountAmount, discountType) => {
  if (discountType === "percentage") {
    return originalPrice * (1 - discountAmount / 100);
  } else if (discountType === "fixed") {
    return Math.max(0, originalPrice - discountAmount);
  }
  return originalPrice;
};

// Get discount code info (client-side only, for display purposes)
export const getDiscountCodeInfo = (code) => {
  return DISCOUNT_CODES[code.toUpperCase()] || null;
};

// Check if user should get special badges based on discount code
export const getBadgeForDiscountCode = (code) => {
  const discountInfo = getDiscountCodeInfo(code);
  return discountInfo?.badge || null;
};

// Apply discount to membership tier
export const applyDiscountToTier = (tierKey, discountCode) => {
  const tier = MEMBERSHIP_TIERS[tierKey.toUpperCase()];
  const discount = getDiscountCodeInfo(discountCode);
  
  if (!tier || !discount) {
    return null;
  }
  
  const discountedPrice = calculateDiscountedPrice(tier.price, discount.value, discount.type);
  
  return {
    ...tier,
    originalPrice: tier.price,
    discountedPrice,
    discount: discount.value,
    discountType: discount.type,
    discountDescription: discount.description,
    badge: discount.badge
  };
};

export default {
  validateDiscountCode,
  calculateDiscountedPrice,
  getDiscountCodeInfo,
  getBadgeForDiscountCode,
  applyDiscountToTier,
  DISCOUNT_CODES
};
